import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  CheckCircle2,
  ExternalLink,
  ImageOff,
  Loader2,
  Plus,
  Upload,
} from "lucide-react";
import type { ScrapedPhoto, WizardState } from "@/lib/newPropertyWizard";

function PhotoTile({
  url,
  selected,
  roomName,
  roomOptions,
  onRoomChange,
  onAddRoom,
}: {
  url: string;
  selected: boolean;
  roomName: string;
  roomOptions: string[];
  onRoomChange: (room: string) => void;
  onAddRoom: (name: string) => void;
}) {
  const [imgError, setImgError] = useState(false);
  const [addingRoom, setAddingRoom] = useState(false);
  const [newRoom, setNewRoom] = useState("");

  return (
    <div
      className={cn(
        "rounded-lg overflow-hidden border-2 transition-all",
        selected ? "border-primary" : "border-transparent opacity-50"
      )}
    >
      <div className="relative aspect-[4/3] bg-muted">
        {imgError ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <ImageOff className="h-5 w-5 text-muted-foreground" />
          </div>
        ) : (
          <img
            src={url}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            onError={() => setImgError(true)}
          />
        )}
        {selected && (
          <div className="absolute top-1.5 right-1.5 bg-green-500 text-white rounded-full p-0.5">
            <CheckCircle2 className="h-3.5 w-3.5" />
          </div>
        )}
      </div>
      <div className="p-1.5 bg-background space-y-1.5">
        {addingRoom ? (
          <div className="flex gap-1">
            <Input
              className="h-7 text-xs"
              value={newRoom}
              onChange={(e) => setNewRoom(e.target.value)}
              placeholder="Room name"
              onKeyDown={(e) => {
                if (e.key === "Enter" && newRoom.trim()) {
                  onAddRoom(newRoom.trim());
                  onRoomChange(newRoom.trim());
                  setAddingRoom(false);
                  setNewRoom("");
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              className="h-7 px-2 text-xs shrink-0"
              onClick={() => {
                if (!newRoom.trim()) return;
                onAddRoom(newRoom.trim());
                onRoomChange(newRoom.trim());
                setAddingRoom(false);
                setNewRoom("");
              }}
            >
              Add
            </Button>
          </div>
        ) : (
          <select
            className="w-full text-xs border rounded px-1.5 py-1.5 bg-background"
            value={selected ? roomName : ""}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__add__") {
                setAddingRoom(true);
                return;
              }
              onRoomChange(v);
            }}
          >
            <option value="">— Skip —</option>
            {roomOptions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
            <option value="__add__">+ Add room…</option>
          </select>
        )}
      </div>
    </div>
  );
}

interface Step2AirbnbProps {
  state: WizardState;
  scrapedPhotos: ScrapedPhoto[];
  detectedRooms: string[];
  assignments: { url: string; roomName: string | null }[];
  onChange: (patch: Partial<WizardState>) => void;
  onImportAirbnb: (url: string) => Promise<void>;
  onManualSetup: () => void;
  onAssignmentsChange: (assignments: { url: string; roomName: string | null }[]) => void;
  onDetectedRoomsChange: (rooms: string[]) => void;
  loading?: boolean;
}

export function Step2Airbnb({
  state,
  scrapedPhotos,
  detectedRooms,
  assignments,
  onChange,
  onImportAirbnb,
  onManualSetup,
  onAssignmentsChange,
  onDetectedRoomsChange,
  loading = false,
}: Step2AirbnbProps) {
  const roomOptions = useMemo(() => {
    const fromAssignments = assignments
      .map((a) => a.roomName)
      .filter((r): r is string => Boolean(r));
    return [...new Set([...detectedRooms, ...fromAssignments])].sort((a, b) =>
      a.localeCompare(b)
    );
  }, [detectedRooms, assignments]);

  useEffect(() => {
    if (scrapedPhotos.length === 0 || assignments.length > 0) return;
    onAssignmentsChange(
      scrapedPhotos.map((p) => ({
        url: p.url,
        roomName: p.roomLabel ?? null,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init once when photos load
  }, [scrapedPhotos]);

  const selectedCount = assignments.filter((a) => a.roomName !== null).length;

  const setPhotoRoom = (url: string, room: string) => {
    onAssignmentsChange(
      assignments.map((a) => {
        if (a.url !== url) return a;
        if (!room) return { ...a, roomName: null };
        return { ...a, roomName: room };
      })
    );
  };

  const addRoomOption = (name: string) => {
    if (!detectedRooms.some((r) => r.toLowerCase() === name.toLowerCase())) {
      onDetectedRoomsChange([...detectedRooms, name]);
    }
  };

  if (state.airbnbImportDone) {
    return (
      <div className="space-y-4 text-center py-8">
        <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto" />
        <h2 className="text-xl font-semibold">Airbnb listing imported</h2>
        <p className="text-muted-foreground text-sm max-w-md mx-auto">
          We imported photos into {state.rooms.length} room
          {state.rooms.length !== 1 ? "s" : ""}. Continue to review and adjust your rooms.
        </p>
      </div>
    );
  }

  if (scrapedPhotos.length > 0) {
    return (
      <div className="space-y-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Rooms &amp; Photos</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Airbnb&apos;s photo tour has{" "}
            <span className="font-semibold text-foreground">{detectedRooms.length} room</span>
            {detectedRooms.length !== 1 ? "s" : ""} and{" "}
            <span className="font-semibold text-foreground">{scrapedPhotos.length} photos</span>.
            Assign each photo to a room in your property, or skip it. If two listing names are the
            same space, use the same room.
          </p>
        </div>

        {roomOptions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {roomOptions.map((room) => {
              const count = assignments.filter((a) => a.roomName === room).length;
              return (
                <span
                  key={room}
                  className="inline-flex items-center rounded-full border bg-muted/50 px-2.5 py-0.5 text-xs font-medium"
                >
                  {room}
                  {count > 0 && (
                    <span className="ml-1 text-muted-foreground">({count})</span>
                  )}
                </span>
              );
            })}
          </div>
        )}

        <div className="max-h-[50vh] overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-2 pr-1">
          {scrapedPhotos.map((photo) => {
            const assignment = assignments.find((a) => a.url === photo.url);
            const roomName = assignment?.roomName ?? "";
            return (
              <PhotoTile
                key={photo.url}
                url={photo.url}
                selected={roomName !== ""}
                roomName={roomName}
                roomOptions={roomOptions}
                onRoomChange={(room) => setPhotoRoom(photo.url, room)}
                onAddRoom={addRoomOption}
              />
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground text-center">
          {selectedCount} of {scrapedPhotos.length} photos will be imported. Click Continue below
          when you&apos;re ready.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Rooms &amp; Photos</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Import room photos and labels from your Airbnb photo tour, or set up manually.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button
          type="button"
          className={cn(
            "flex flex-col items-start gap-3 p-4 sm:p-5 rounded-xl border-2 text-left transition-all",
            "border-[#FF5A5F] bg-[#FF5A5F]/5"
          )}
        >
          <div className="flex items-center gap-2 text-[#FF5A5F] font-semibold">
            <ExternalLink className="h-5 w-5" />
            Import from Airbnb
          </div>
          <p className="text-sm text-muted-foreground">
            Pull room tour photos only — not marketing hero images.
          </p>
        </button>

        <button
          type="button"
          onClick={onManualSetup}
          disabled={loading}
          className={cn(
            "flex flex-col items-start gap-3 p-4 sm:p-5 rounded-xl border-2 text-left transition-all",
            "border-border hover:border-muted-foreground/50"
          )}
        >
          <div className="flex items-center gap-2 font-semibold text-muted-foreground">
            <Upload className="h-5 w-5" />
            Set up manually
          </div>
          <p className="text-sm text-muted-foreground">
            Enter your rooms and upload photos yourself.
          </p>
        </button>
      </div>

      <div className="space-y-3 rounded-lg border p-4 sm:p-6 bg-card">
        <div className="space-y-2">
          <Label htmlFor="airbnb-url">Airbnb listing URL</Label>
          <Input
            id="airbnb-url"
            value={state.airbnbUrl}
            onChange={(e) => onChange({ airbnbUrl: e.target.value })}
            placeholder="https://www.airbnb.com/rooms/..."
            inputMode="url"
          />
        </div>
        <Button
          type="button"
          className="bg-[#FF5A5F] hover:bg-[#FF5A5F]/90 text-white w-full sm:w-auto"
          disabled={loading || !state.airbnbUrl.trim()}
          onClick={() => onImportAirbnb(state.airbnbUrl.trim())}
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading room photos…
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-2" />
              Load room photos
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
